import ImpTracker from '@/components/common/ImpTracker';
import TimeCountDown from '@/components/common/TimeCountDown';
import { BannerType } from '@/constants/track';
import useExpRedirect from '@/hooks/useExpRedirect';
import useHomepage from '@/store/home';
import useClickActionT2 from '@/tracks/useClickActionT2';
import useImpressionT2 from '@/tracks/useImpressionT2';
import { ComponentProps } from '@/types/cms';
import classNames from 'classnames';
import router from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import withFetch from '../../hooks/fetch';
import PopupNotice from './PopupNotice';
import TopMessageSkeleton from './skeleton';
import { CroppedImage } from '@weee-fe/module';
import { useKeepAliveMountEffect, useKeepAliveUnmountEffect } from '@/hooks/keepalive/hooks';
import Container from '../Layout/container';

const TopMessage: React.FC<ComponentProps> = props => {
  const [visible, setVisible] = useState(false);
  const [popUpData, setPopUpData] = useState<any>(null);
  const isKeepAliveUnmount = useRef<boolean>(false);
  const doExperimentRedirect = useExpRedirect();
  const { bannerImpression } = useImpressionT2();
  const { bannerClick } = useClickActionT2();
  const refTimeStamp = useRef({
    countdown: 0,
    index: 0
  });
  const refTimer = useRef(null);
  const { isStatic, postUrl, componentPosition } = props;
  const { store } = useHomepage();
  const { recommendBannerVisible } = store;
  const targetKey = props.properties?.event_key || props.cmsComponentKey;

  const visibleCountDown = useMemo(() => {
    const data = popUpData?.find(item => (item?.type as number) === 1);
    if (!!data?.sys_time && !!data?.countdown?.end_time) {
      let range = (+data?.countdown?.end_time - +data?.sys_time) / 3600 > 24;
      return !range;
    }
    return false;
  }, [JSON.stringify(popUpData)]);

  const { data, load, mutate } = withFetch({
    data: props.data,
    isStatic: isStatic ?? false,
    postUrl: postUrl
    // componentDataSourceKey
  });

  // 应用keepalive方案之后，首页返回需要重新请求，更新消息组件内容
  useKeepAliveMountEffect('cms-home-page', () => {
    if (isKeepAliveUnmount.current) {
      mutate();
    }
  });

  useKeepAliveUnmountEffect('cms-home-page', () => {
    isKeepAliveUnmount.current = true;
  });

  useEffect(() => {
    let newData = data;
    if (newData && typeof newData === 'object') {
      if (!Array.isArray(newData)) {
        //兼容数据为对象的情况
        newData = [data];
      }
    }
    //当展示返利联盟的referral-banner时隐藏注册立减banner
    if (/login$/g.test(newData?.[0]?.url ?? '') && recommendBannerVisible) {
      setVisible(false);
      return;
    }
    newData?.forEach((item, index) => {
      if (item.countdown) {
        refTimeStamp.current = {
          countdown: item.countdown,
          index
        };
      }
    });
    refTimer.current = setInterval(() => {
      refTimeStamp.current.countdown = refTimeStamp.current.countdown - 1;
    }, 1000);
    setPopUpData(newData);

    return () => {
      clearInterval(refTimer.current);
      refTimer.current = null;
    };
  }, [JSON.stringify(data), recommendBannerVisible]);

  const onClose = () => {
    setVisible(false);
  };
  if (load && props.isLogin) {
    return (
      <Container className="bg-surface-100-bg z-[1] relative min-h-[54px] px-4 pt-1">
        <TopMessageSkeleton />
      </Container>
    );
  }
  if (!popUpData?.length) {
    return null;
  }

  return (
    <>
      {visible && <PopupNotice visible={visible} onClose={onClose} data={popUpData} />}
      <Container
        className="bg-surface-100-bg z-[1] relative min-h-[54px] px-4 pt-1"
        data-testid="mod-top-message"
        style={{ WebkitTextSizeAdjust: 'none', textSizeAdjust: 'none' }}>
        <div className={twMerge('bg-surface-200-bg rounded-xl')}>
          {popUpData?.slice(0, 1).map((item, index) => {
            const { background, countdown, message, right_cta, icon_img, link, type, sys_time, progress } = item;
            const combineCo =
              !!message?.sub_content || visibleCountDown
                ? {
                    target_type: 'message',
                    click_type: 'view',
                    is_select: null,
                    target_pos: null,
                    target_nm: 'add_on_item'
                  }
                : {};
            return (
              <div
                data-testid={`wid-top-message-item-${index}`}
                onClick={() => {
                  //EC-5122: 点击popup直接跳转不弹出popup
                  if (Number(type) === 1 && link) {
                    location.href = link;
                    return;
                  }
                  if (item?.message_type === 'login') {
                    const destUrl = doExperimentRedirect();

                    if (destUrl) {
                      router.push(destUrl);
                      return;
                    }
                  }

                  if (refTimeStamp.current?.countdown) {
                    const d = [...popUpData];
                    d[refTimeStamp.current.index].countdown = refTimeStamp.current.countdown;

                    setPopUpData(d);
                  }
                  setVisible(true);
                }}
                className={'relative rounded-xl px-3'}
                style={{
                  backgroundColor: background?.color || '#f3f3f3',
                  backgroundImage: `url(${background?.img || ''})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover'
                }}
                key={index}>
                <div
                  className={twMerge(
                    classNames('h-[44px] py-1 relative flex justify-between items-center gap-2', {
                      'text-center':
                        !message?.sub_message && !icon_img && !message?.sub_content && !visibleCountDown && !progress
                    })
                  )}>
                  <ImpTracker
                    className="flex items-center flex-1 gap-2"
                    impTrackingT2={() => {
                      bannerImpression({
                        mod_nm: targetKey,
                        mod_pos: componentPosition,
                        co: {
                          banner_key: message?.short_message,
                          banner_pos: index,
                          banner_type: BannerType.homeMessage,
                          url: link
                        }
                      });
                    }}
                    clickTrackingT2={() => {
                      bannerClick({
                        mod_nm: targetKey,
                        mod_pos: componentPosition,
                        co: {
                          banner_key: message?.short_message,
                          banner_type: BannerType.homeMessage,
                          ...combineCo,
                          url: link
                        }
                      });
                    }}>
                    {!!icon_img && (
                      <CroppedImage
                        data-testid="wid-top-message-icon"
                        wrapperClassName={'w-[35px] h-[35px] rounded-200 flex-none'}
                        className="rounded-200"
                        url={icon_img}
                        configs={{
                          renderHeight: 35,
                          renderWidth: 35
                        }}
                        width={35}
                        height={35}
                        showLoadingPlaceholder={false}
                        alt="top message icon"
                      />
                    )}
                    <div className="max-h-[44px] flex flex-col justify-start flex-1 enki-body-3xs-medium text-ellipsis line-clamp-3">
                      {!!message?.short_message && (
                        <div
                          data-testid="wid-top-message-content"
                          className={twMerge(!!message?.sub_message && 'text-ellipsis line-clamp-2')}
                          dangerouslySetInnerHTML={{ __html: message?.short_message }}></div>
                      )}
                      {!!message?.sub_message && (
                        <div
                          data-testid="wid-top-message-sub-content"
                          dangerouslySetInnerHTML={{ __html: message?.sub_message }}></div>
                      )}
                    </div>
                    {!!message?.sub_content ? (
                      <div
                        data-testid="wid-top-message-sub-content"
                        className="w-16 enki-body-3xs-strong text-center"
                        style={{ zoom: 0.9 }}
                        dangerouslySetInnerHTML={{ __html: message?.sub_content }}></div>
                    ) : (
                      <>
                        {visibleCountDown && (
                          <div
                            data-testid="wid-top-message-time-count-down"
                            style={{ color: countdown?.number_color ? countdown?.number_color : '#027fff' }}
                            className="w-16 text-xs">
                            <TimeCountDown startTime={sys_time} endTime={countdown?.end_time} showDay={false} />
                          </div>
                        )}
                      </>
                    )}
                  </ImpTracker>
                  {/* type 1图片 2文案 */}
                  {right_cta?.type === 1 && !!right_cta?.img && (
                    <img
                      data-testid="wid-top-message-right-cta-image"
                      className="inline-block w-5 h-5"
                      src={right_cta?.img}
                      alt="Right cta image"
                    />
                  )}
                  {right_cta?.type === 2 && !!right_cta?.title && (
                    <span
                      data-testid="wid-top-message-right-cta-title"
                      className="enki-body-3xs"
                      dangerouslySetInnerHTML={{ __html: right_cta?.title }}
                      style={{ color: right_cta?.title_color }}
                    />
                  )}
                </div>
                {progress && (
                  <div data-testid="wid-top-message-progress">
                    <div
                      className="h-1 rounded-full relative"
                      style={{
                        backgroundColor: progress.background_color
                      }}>
                      <div
                        className="rounded-full absolute left-0 top-0 bottom-0"
                        style={{
                          backgroundColor: progress.foreground_color,
                          width: `${progress.progress_value}%`
                        }}
                      />
                    </div>
                    <div
                      data-testid="wid-top-message-progress-title"
                      dangerouslySetInnerHTML={{ __html: progress.title }}
                      className="enki-body-2xs-strong text-center py-2"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Container>
    </>
  );
};

export default React.memo(TopMessage);
